import React from 'react';
import { m } from 'framer-motion';
import { Sun, Moon } from 'lucide-react';

interface ThemeToggleProps {
  theme: 'light' | 'dark';
  onChange: (theme: 'light' | 'dark') => void;
  disabled?: boolean;
  className?: string;
}

const ThemeToggle: React.FC<ThemeToggleProps> = ({
  theme,
  onChange,
  disabled = false,
  className = '',
}) => {
  const toggleTheme = () => {
    if (!disabled) {
      onChange(theme === 'light' ? 'dark' : 'light');
    }
  };

  const handleToggleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
      e.preventDefault();
      onChange(theme === 'light' ? 'dark' : 'light');
    }
  };

  return (
    <button
      type="button"
      onClick={toggleTheme}
      onKeyDown={handleToggleKeyDown}
      disabled={disabled}
      className={`
        relative inline-flex items-center rounded-lg p-1 transition-all duration-200 ease-in-out
        focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
        dark:focus:ring-offset-zinc-900
        bg-zinc-100 dark:bg-zinc-700 hover:bg-zinc-200 dark:hover:bg-zinc-600
        hover:shadow-sm active:scale-95
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${className}
      `}
      style={{ width: '64px', height: '32px' }}
    >
      {/* Background sliding indicator */}
      <m.div
        className="absolute top-1 bottom-1 left-1 rounded-md bg-white dark:bg-zinc-800 shadow-sm"
        animate={{
          x: theme === 'dark' ? '100%' : '0%',
        }}
        transition={{
          type: 'spring',
          stiffness: 500,
          damping: 30,
        }}
        style={{ width: 'calc(50% - 4px)' }}
      />
      
      {/* Light/Sun Icon */}
      <div className="relative z-10 flex-1 flex items-center justify-center">
        <m.div
          animate={{
            scale: theme === 'light' ? 1.1 : 0.9,
            rotate: theme === 'light' ? 0 : -180,
          }}
          transition={{ 
            duration: 0.3,
            ease: 'easeInOut'
          }}
        >
          <Sun 
            className={`size-3.5 transition-colors duration-200 ${
              theme === 'light'
                ? 'text-amber-500'
                : 'text-zinc-400 dark:text-zinc-500'
            }`}
          />
        </m.div>
      </div>
      
      {/* Dark/Moon Icon */}
      <div className="relative z-10 flex-1 flex items-center justify-center">
        <m.div
          animate={{
            scale: theme === 'dark' ? 1.1 : 0.9,
            rotate: theme === 'dark' ? 0 : 180,
          }}
          transition={{ 
            duration: 0.3,
            ease: 'easeInOut'
          }}
        >
          <Moon 
            className={`size-3.5 transition-colors duration-200 ${
              theme === 'dark'
                ? 'text-blue-400'
                : 'text-zinc-400 dark:text-zinc-500'
            }`}
          />
        </m.div>
      </div>
    </button>
  );
};

export default ThemeToggle;
